import {
  FeedbackItemStatistics,
  FeedbackItem,
  FeedbackResourceType,
  SubmitFeedbackRequest,
  ListFeedbackRequest,
  DeleteFeedbackRequest,
  GetFeedbackByUserRequest,
} from '@amzn/awscat-pattern-library-service-model';
import { flashbar } from '../../decorators/flashbar';
import { IFeedbackServiceClient, type SubmitFeedbackOptions } from './IFeedbackServiceClient';
import {
  FeedbackRating,
  FeedbackRatingSystem,
  FeedbackSource,
} from '../../components/feedback-service';
import { MplServiceClientFactory } from '../MplServiceClientFactory';
import { IMplServiceClient } from '../IMplServiceClient';

const sourceApp = 'apglibrary';
class FeedbackServiceClient implements IFeedbackServiceClient {
  private mplService: IMplServiceClient;

  public constructor() {
    this.mplService = MplServiceClientFactory.create();
  }

  @flashbar({ error: 'Failed to provide feedback' })
  public async submitFeedback(
    resourceId: string,
    userId: string,
    rating: FeedbackRating,
    ratingSystem: FeedbackRatingSystem,
    resourceType: FeedbackResourceType,
    { comment = '', metadata, feedbackSource }: SubmitFeedbackOptions = {},
  ): Promise<FeedbackItemStatistics> {
    // Leave out metadata without values (e.g. a collection rating, or a `NaN` content version parsed from a URL
    // without one), so no empty map is stored
    const hasMetadata = Object.values(metadata ?? {}).some(
      (value) => value !== undefined && !Number.isNaN(value),
    );

    const requestData = Object.assign(new SubmitFeedbackRequest(), {
      srcURL: window?.location?.href ?? '',
      srcApp: sourceApp,
      ratingSystem,
      rating,
      comment,
      resourceOwner: '',
      resourceId,
      feedbackSource,
      resourceType,
      metadata: hasMetadata ? metadata : undefined,
    });
    const response = await this.mplService.submitFeedback(requestData);
    return response;
  }

  @flashbar({ error: 'Failed to delete feedback' })
  public async deleteFeedback(
    resourceId: string,
    ratingSystem: FeedbackRatingSystem,
    feedbackSource?: FeedbackSource,
  ): Promise<FeedbackItemStatistics> {
    const deleteRequest = Object.assign(new DeleteFeedbackRequest(), {
      resourceId,
      ratingSystem,
      feedbackSource,
    });
    const response = await this.mplService.deleteFeedback(deleteRequest);
    return response;
  }

  @flashbar({
    // @ts-expect-error TS2322 - strict migration
    error: (e) =>
      e?.response?.status >= 500
        ? { status: e?.response?.status, message: 'Failed list feedback.' }
        : null,
  })
  public async listFeedback(
    resourceIds: string[],
    ratingSystem: FeedbackRatingSystem,
    feedbackSource?: FeedbackSource,
  ): Promise<Array<FeedbackItemStatistics | null>> {
    const request: ListFeedbackRequest = Object.assign(new ListFeedbackRequest(), {
      resourceIds,
      feedbackSource,
      ratingSystem,
    });
    const response = await this.mplService.listFeedback(request);
    return response;
  }

  @flashbar({ error: 'Failed to get feedback for user' })
  public async getFeedbackByUser(
    resourceId: string,
    userId: string,
    feedbackSource?: FeedbackSource,
  ): Promise<FeedbackItem | null> {
    const request = Object.assign(new GetFeedbackByUserRequest(), {
      resourceId,
      feedbackSource,
    });
    const response = await this.mplService.getFeedbackByUser(request, userId);
    return response;
  }
}

export default FeedbackServiceClient;
